/**
 * Cover Press — the playlist-art press engine.
 *
 * Every cover is one pull of a press: a FORM makes the shapes, a SEPARATION
 * cuts them into ink plates, an IMPRESSION decides how the ink lands, and the
 * PAPER shows through everywhere the roller ran dry. The pipeline is
 * deterministic — a recipe (colourway + plate order + seed + params) always
 * paints the same pixels, at any output size, so the studio preview and the
 * exported PNG match exactly.
 *
 * Four staged registries are the extension points:
 *   FORMS      add a shape source: {label, sep, field(px,py,seed,P)}
 *   SCREENS    add a screening method: {label, punch(...) -> punch to paper?}
 *   WAYS       add a colourway: {name, ref, paper, inks:[[rampA,rampB],...]}
 *              — 1 ink = duotone, 2–3 inks = plate stack
 *   FORMATS    add an output format: {label, w, h, note, post?} — post masks
 *              or frames the pull for its surface
 *   ARCHETYPES add a starting recipe: {label, way, p:{...params}}
 * Nothing else needs touching to extend any of them.
 *
 * This module is DOM-free apart from the canvas it is handed, so it can run
 * on the page or in a worker.
 */

export type FormKey =
  | "contour"
  | "current"
  | "ripple"
  | "eddy"
  | "shard"
  | "pool"
export type ScreenKey = "solid" | "wear"
export type WayKey =
  | "petrol"
  | "peri"
  | "coral"
  | "jade"
  | "rose"
  | "gold"
  | "plum"
  | "press"
  | "cinder"
  | "moss"
  | "tide"
  | "reef"
  | "ember"
  | "hedge"
  | "wheel"
  | "dusk"
  | "harvest"
export type FormatKey = "cover" | "card" | "story" | "poster" | "cd" | "back"
export type ArchetypeKey = "topo" | "melt" | "camo" | "triple"

/** The tunable half of a recipe — everything the sliders and chips drive. */
export interface Params {
  pattern: FormKey
  /** Band density, 2–9. */
  busy: number
  /** Domain-warp strength, 0–1. */
  molten: number
  /** Vertical field gradient, -1–1. */
  drift: number
  /** Focal-lens pull, 0–1. */
  focus: number
  /** Focal cell on a 3×3 grid, 0–8 (row-major, 4 = centre). */
  focal: number
  screen: ScreenKey
  /** Wear depth, 0–1. */
  worn: number
  /** Dry-roller lane count, 2–30. */
  lanes: number
  /** Ink coverage across lanes, 0.1–1. */
  laneCover: number
  /** Roller angle in degrees, 0–180. */
  laneAngle: number
  /** Registration offset, 0–1. */
  register: number
  /** Paper tooth, 0–1. */
  grain: number
}

/** A full, reproducible pull. */
export interface Recipe {
  way: WayKey
  /** Plate-order permutation index (only meaningful for 2–3 ink ways). */
  perm: number
  seed: number
  format: FormatKey
  p: Params
}

type Rgb = [number, number, number]
type Ramp = [string, string]

export interface Way {
  name: string
  ref: string
  paper: string
  inks: Ramp[]
}

interface Form {
  label: string
  sep: "bands" | "pools"
  /** Band density from the "how busy" knob; defaults to the knob itself. */
  dens?: (busy: number) => number
  field: (px: number, py: number, seed: number, P: Params) => number
}

interface ScreenEnv {
  seed: number
  s01: number
  worn: number
  edge: number
  clean: number
  ldx: number
  ldy: number
}

interface Screen {
  label: string
  punch: (
    sd: number,
    lane: number,
    x: number,
    y: number,
    plate: number,
    E: ScreenEnv,
  ) => boolean
}

export interface Format {
  label: string
  w: number
  h: number
  note: string
  post?: (ctx: CanvasRenderingContext2D, w: number, h: number, way: Way) => void
}

export interface Archetype {
  label: string
  way: WayKey
  p: Omit<Params, "focal" | "grain">
}

export const WAYS: Record<WayKey, Way> = {
  petrol: {
    name: "Astral Petrol",
    ref: "celestial duotone",
    paper: "#E6F4F4",
    inks: [["#2F6B77", "#4C96A3"]],
  },
  peri: {
    name: "Dusk Periwinkle",
    ref: "coded grid",
    paper: "#EEF0FA",
    inks: [["#7C86B6", "#4F5A94"]],
  },
  coral: {
    name: "Crush Coral",
    ref: "fluid bands",
    paper: "#FFE6CF",
    inks: [["#CE8067", "#A95F4A"]],
  },
  jade: {
    name: "Saville Jade",
    ref: "wheel green",
    paper: "#DFF0E9",
    inks: [["#4F8B7A", "#2F5D50"]],
  },
  rose: {
    name: "Fantin Rose",
    ref: "painterly bloom",
    paper: "#F6E6EA",
    inks: [["#C1848D", "#9D6470"]],
  },
  gold: {
    name: "Halsall Gold",
    ref: "rising sun",
    paper: "#FBF0CF",
    inks: [["#C6A04D", "#9C7A34"]],
  },
  plum: {
    name: "Nightshade Plum",
    ref: "house original",
    paper: "#E9D2DA",
    inks: [["#3A2547", "#5E3B66"]],
  },
  press: {
    name: "Press Black",
    ref: "single-plate proof",
    paper: "#FAF7F0",
    inks: [["#20202B", "#3C3C4A"]],
  },
  cinder: {
    name: "Cinder Blue",
    ref: "ink-dark nocturne",
    paper: "#E2E6EE",
    inks: [["#33415E", "#54689A"]],
  },
  moss: {
    name: "Bottle Moss",
    ref: "deep field green",
    paper: "#E8EBD9",
    inks: [["#55613A", "#7C8A55"]],
  },
  tide: {
    name: "Tide Two-Plate",
    ref: "petrol + gold stack",
    paper: "#EFEAD8",
    inks: [
      ["#2F6B77", "#4C96A3"],
      ["#C79A4B", "#E0BE6A"],
    ],
  },
  reef: {
    name: "Reef Two-Plate",
    ref: "coral + periwinkle stack",
    paper: "#F7EEE3",
    inks: [
      ["#CE8067", "#A95F4A"],
      ["#7C86B6", "#4F5A94"],
    ],
  },
  ember: {
    name: "Ember Two-Plate",
    ref: "plum + gold stack",
    paper: "#F0E4D6",
    inks: [
      ["#5E3B66", "#3A2547"],
      ["#C6A04D", "#E0BE6A"],
    ],
  },
  hedge: {
    name: "Hedgerow Two-Plate",
    ref: "moss + rose stack",
    paper: "#F1EFDF",
    inks: [
      ["#55613A", "#7C8A55"],
      ["#C1848D", "#9D6470"],
    ],
  },
  wheel: {
    name: "Wheel Triplet",
    ref: "three-plate stack",
    paper: "#F4EDE2",
    inks: [
      ["#4F8B7A", "#2F5D50"],
      ["#C6A04D", "#9C7A34"],
      ["#C1848D", "#9D6470"],
    ],
  },
  dusk: {
    name: "Dusk Triplet",
    ref: "petrol + plum + rose stack",
    paper: "#EDE9E4",
    inks: [
      ["#2F6B77", "#4C96A3"],
      ["#5E3B66", "#3A2547"],
      ["#C1848D", "#9D6470"],
    ],
  },
  harvest: {
    name: "Harvest Triplet",
    ref: "gold + coral + moss stack",
    paper: "#F6EFDB",
    inks: [
      ["#C6A04D", "#9C7A34"],
      ["#CE8067", "#A95F4A"],
      ["#55613A", "#7C8A55"],
    ],
  },
}

/** Every pull order for a 2- or 3-plate stack. Single-plate ways have none. */
export const PERMS: Record<number, number[][]> = {
  2: [
    [0, 1],
    [1, 0],
  ],
  3: [
    [0, 1, 2],
    [0, 2, 1],
    [1, 0, 2],
    [1, 2, 0],
    [2, 0, 1],
    [2, 1, 0],
  ],
}

export function permsFor(way: Way): number[][] | undefined {
  return PERMS[way.inks.length]
}

/** The way with its plates reordered by permutation `perm` (mod the list). */
export function permWay(way: Way, perm: number): Way {
  const list = permsFor(way)
  if (!list) return way
  const pm = list[(perm || 0) % list.length]
  return { ...way, inks: pm.map((i) => way.inks[i]) }
}

function hex(h: string): Rgb {
  const n = parseInt(h.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

export function clampN(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

/** Deterministic 2D integer hash → [0,1). */
export function hash2(x: number, y: number, s: number): number {
  let h =
    Math.imul(x | 0, 374761393) +
    Math.imul(y | 0, 668265263) +
    Math.imul(s | 0, 1013904223)
  h = h ^ (h >>> 13)
  h = Math.imul(h, 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295
}

/** Smoothstep-interpolated value noise. */
function vn(x: number, y: number, s: number): number {
  const xi = Math.floor(x)
  const yi = Math.floor(y)
  const xf = x - xi
  const yf = y - yi
  const u = xf * xf * (3 - 2 * xf)
  const v = yf * yf * (3 - 2 * yf)
  const a = hash2(xi, yi, s)
  const b = hash2(xi + 1, yi, s)
  const c = hash2(xi, yi + 1, s)
  const d = hash2(xi + 1, yi + 1, s)
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v
}

function fbm(x: number, y: number, s: number, oct = 3, rough = 0.5): number {
  const n = Math.max(1, Math.round(oct))
  let sum = 0
  let amp = 1
  let f = 1
  let norm = 0
  for (let i = 0; i < n; i++) {
    sum += amp * vn(x * f, y * f, s + i * 101)
    norm += amp
    amp *= rough
    f *= 2
  }
  return sum / norm
}

/**
 * Ripple's two drop-points depend only on the seed, so they are memoised per
 * seed rather than recomputed for every pixel.
 */
let rippleCache: { s: number; pts: [number, number][] } | null = null
function rippleDrops(s: number): [number, number][] {
  if (rippleCache?.s !== s) {
    rippleCache = {
      s,
      pts: [0, 1].map((i): [number, number] => [
        0.75 + (fbm(i * 3.7 + 1.3, i * 1.9 + 6.2, s + 83, 1) - 0.5) * 1.0,
        0.75 + (fbm(i * 5.1 + 8.8, i * 2.3 + 2.4, s + 97, 1) - 0.5) * 1.0,
      ]),
    }
  }
  return rippleCache.pts
}

export const FORMS: Record<FormKey, Form> = {
  contour: {
    label: "Contour",
    sep: "bands",
    field(px, py, s, P) {
      const w1 = fbm(px * 0.8 + 3.1, py * 0.8 + 7.7, s + 11, 2) - 0.5
      const w2 = fbm(px * 0.8 + 9.2, py * 0.8 + 2.3, s + 23, 2) - 0.5
      const wp = 0.35 + P.molten * 1.9
      return fbm(px + wp * w1, py + wp * w2, s, 3, 0.4 + P.molten * 0.3)
    },
  },
  current: {
    label: "Current",
    sep: "bands",
    field(px, py, s, P) {
      const wy = fbm(px * 0.6 + 5.2, py * 0.6 + 1.7, s + 31, 2) - 0.5
      const wx = fbm(px * 0.9 + 2.2, py * 0.9 + 8.8, s + 47, 2) - 0.5
      return fbm(
        px * 0.45 + P.molten * 0.9 * wx,
        py * 1.45 + (0.6 + P.molten * 1.6) * wy,
        s,
        3,
        0.5,
      )
    },
  },
  ripple: {
    label: "Ripple",
    sep: "bands",
    dens: (b) => 0.4 + (b - 2) * 0.15,
    field(px, py, s, P) {
      // Interference: seeded drop-points, colliding wavefronts with distance
      // falloff. One dominant ring system, a second faint source bending it
      // where they meet.
      const C = rippleDrops(s)
      const w1 = fbm(px * 0.9 + 2.6, py * 0.9 + 5.9, s + 17, 2) - 0.5
      const w2 = fbm(px * 0.9 + 7.4, py * 0.9 + 1.2, s + 29, 2) - 0.5
      const wp = 0.05 + P.molten * 0.22
      const x = px + wp * w1
      const y = py + wp * w2
      const d0x = x - C[0][0]
      const d0y = y - C[0][1]
      const r0 = Math.sqrt(d0x * d0x + d0y * d0y)
      const d1x = x - C[1][0]
      const d1y = y - C[1][1]
      const r1 = Math.sqrt(d1x * d1x + d1y * d1y)
      const bend = (0.1 + P.molten * 0.25) / (1 + r1 * 2.5)
      return Math.min(
        1,
        (r0 + bend * Math.cos(r1 * 5 * Math.PI) + w1 * 0.06) / 1.02,
      )
    },
  },
  eddy: {
    label: "Eddy",
    sep: "bands",
    dens: (b) => 0.4 + (b - 2) * 0.15,
    field(px, py, s, P) {
      const dx = px - 0.75
      const dy = py - 0.75
      const r = Math.sqrt(dx * dx + dy * dy)
      const a = Math.atan2(dy, dx) + (1.2 + P.molten * 3.2) * (1 - r / 1.06) * 2
      return fbm(
        0.75 + r * Math.cos(a) + 1.8,
        0.75 + r * Math.sin(a) + 4.4,
        s,
        3,
        0.45,
      )
    },
  },
  shard: {
    label: "Shard",
    sep: "pools",
    field(px, py, s, P) {
      const f = 0.6 + P.busy * 0.18
      const w1 = fbm(px * 0.8 + 6.2, py * 0.8 + 3.7, s + 41, 2) - 0.5
      const w2 = fbm(px * 0.8 + 1.9, py * 0.8 + 9.4, s + 67, 2) - 0.5
      const wp = 0.2 + P.molten * 0.6
      const v = fbm(
        px * f + wp * w1 + 3.3,
        py * f + wp * w2 + 8.1,
        s + 53,
        3,
        0.5,
      )
      // Ridged + sharpened, restretched to span 0..1 so the pool slicer cuts
      // distinct shards.
      return Math.min(1, Math.pow(1 - Math.abs(v - 0.5) * 2, 3) * 1.6)
    },
  },
  pool: {
    label: "Pool",
    sep: "pools",
    field(px, py, s, P) {
      const f = 0.55 + P.busy * 0.22
      const w1 = fbm(px * 0.7 + 4.4, py * 0.7 + 6.1, s + 13, 2) - 0.5
      const w2 = fbm(px * 0.7 + 8.3, py * 0.7 + 3.9, s + 29, 2) - 0.5
      const wp = 0.3 + P.molten * 1.5
      return fbm((px + wp * w1) * f, (py + wp * w2) * f, s, 3, 0.5)
    },
  },
}

export const SCREENS: Record<ScreenKey, Screen> = {
  solid: {
    label: "Solid",
    punch() {
      return false
    },
  },
  wear: {
    label: "Worn",
    punch(sd, lane, x, y, q, E) {
      if (lane <= 0 || sd < E.clean * 0.5) return false
      const u = (sd - E.clean) / E.edge
      const ew = u < 0 ? 1 - u * u * 0.65 : Math.exp(-u * u * 1.6)
      const sp = vn((x * 1.9) / E.s01, (y * 1.9) / E.s01, E.seed + 99 + q * 7)
      const cp = x * E.ldx + y * E.ldy
      const cq = -x * E.ldy + y * E.ldx
      const cl = vn((cp * 0.35) / E.s01, (cq * 4.5) / E.s01, E.seed + 131)
      return sp < (0.1 + E.worn * 0.7) * ew * lane * (0.45 + cl)
    },
  },
}

/** Pool separation: field-value slices per plate, by ink count. */
const SLICES: Record<number, [number, number][]> = {
  1: [[-9, 0.47]],
  2: [
    [-9, 0.42],
    [0.42, 0.56],
  ],
  3: [
    [-9, 0.38],
    [0.38, 0.5],
    [0.5, 0.6],
  ],
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/** CD label: keep the disc, cut the centre hole. */
function discMask(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.globalCompositeOperation = "destination-in"
  ctx.beginPath()
  ctx.arc(w / 2, h / 2, w / 2, 0, 6.2832)
  ctx.fill()
  ctx.globalCompositeOperation = "destination-out"
  ctx.beginPath()
  ctx.arc(w / 2, h / 2, w * 0.174, 0, 6.2832)
  ctx.fill()
  ctx.globalCompositeOperation = "source-over"
}

/** Card back: paper margin, keyline in the first ink, rounded corners. */
function cardBackFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  way: Way,
) {
  const mrg = Math.round(w * 0.055)
  const rO = Math.round(w * 0.06)
  const rI = Math.round(w * 0.025)
  ctx.fillStyle = "#FFFDF6"
  ctx.beginPath()
  ctx.rect(0, 0, w, h)
  roundedRectPath(ctx, mrg, mrg, w - 2 * mrg, h - 2 * mrg, rI)
  ctx.fill("evenodd")
  ctx.strokeStyle = way.inks[0][0]
  ctx.lineWidth = Math.max(2, w * 0.004)
  ctx.beginPath()
  roundedRectPath(ctx, mrg, mrg, w - 2 * mrg, h - 2 * mrg, rI)
  ctx.stroke()
  ctx.globalCompositeOperation = "destination-in"
  ctx.beginPath()
  roundedRectPath(ctx, 0, 0, w, h, rO)
  ctx.fill()
  ctx.globalCompositeOperation = "source-over"
}

export const FORMATS: Record<FormatKey, Format> = {
  cover: { label: "Cover", w: 1600, h: 1600, note: "square streaming artwork" },
  card: {
    label: "Playlist card",
    w: 1200,
    h: 560,
    note: "wide web card, 2x density",
  },
  story: { label: "Story", w: 1080, h: 1920, note: "9:16 vertical for social" },
  poster: {
    label: "Poster",
    w: 1800,
    h: 2700,
    note: "12 × 18 in print at 150 dpi",
  },
  cd: {
    label: "CD label",
    w: 1400,
    h: 1400,
    note: "118 mm printable disc face at 300 dpi, hole cut",
    post: discMask,
  },
  back: {
    label: "Card back",
    w: 750,
    h: 1050,
    note: "2.5 × 3.5 in at 300 dpi, bordered with keyline",
    post: cardBackFrame,
  },
}

export const ARCHETYPES: Record<ArchetypeKey, Archetype> = {
  topo: {
    label: "Topo lines",
    way: "petrol",
    p: {
      pattern: "contour",
      busy: 4,
      molten: 0.5,
      drift: 0,
      focus: 0.25,
      screen: "wear",
      worn: 0.5,
      lanes: 9,
      laneCover: 0.55,
      laneAngle: 0,
      register: 0,
    },
  },
  melt: {
    label: "Meltwater",
    way: "coral",
    p: {
      pattern: "current",
      busy: 3,
      molten: 0.85,
      drift: 0.3,
      focus: 0.15,
      screen: "wear",
      worn: 0.45,
      lanes: 6,
      laneCover: 0.6,
      laneAngle: 90,
      register: 0,
    },
  },
  camo: {
    label: "Camo pool",
    way: "jade",
    p: {
      pattern: "pool",
      busy: 3,
      molten: 0.6,
      drift: 0,
      focus: 0.3,
      screen: "wear",
      worn: 0.7,
      lanes: 14,
      laneCover: 0.75,
      laneAngle: 25,
      register: 0,
    },
  },
  triple: {
    label: "Three-plate",
    way: "wheel",
    p: {
      pattern: "contour",
      busy: 5,
      molten: 0.55,
      drift: 0,
      focus: 0.2,
      screen: "solid",
      worn: 0.3,
      lanes: 8,
      laneCover: 0.5,
      laneAngle: 0,
      register: 0.35,
    },
  },
}

export const DEFAULT_PARAMS: Params = {
  pattern: "contour",
  busy: 4,
  molten: 0.5,
  drift: 0,
  focus: 0.25,
  focal: 4,
  screen: "wear",
  worn: 0.5,
  lanes: 9,
  laneCover: 0.55,
  laneAngle: 0,
  register: 0,
  grain: 0.35,
}

export const DEFAULT_RECIPE: Recipe = {
  way: "petrol",
  perm: 0,
  seed: 7412,
  format: "cover",
  p: { ...DEFAULT_PARAMS },
}

/** The effective (plate-ordered) colourway for a recipe. */
export function effectiveWay(recipe: Pick<Recipe, "way" | "perm">): Way {
  return permWay(WAYS[recipe.way], recipe.perm)
}

/**
 * One pull of the press: form → separation → impression → substrate.
 * All px-based effects scale with s01 = max(w,h)/560, so a small preview and
 * a full-size export of the same recipe look identical.
 */
export function paint(
  cv: HTMLCanvasElement,
  w: number,
  h: number,
  P: Params,
  way: Way,
  seed: number,
) {
  cv.width = w
  cv.height = h
  const ctx = cv.getContext("2d")
  if (!ctx) return
  const m = Math.max(w, h)
  const s01 = m / 560
  const form = FORMS[P.pattern] ?? FORMS.contour

  // FORM: field buffer, with focal lens + drift baked into sampling
  const F = new Float32Array(w * h)
  const cxp = w * (0.5 + ((P.focal % 3) - 1) * 0.3)
  const cyp = h * (0.5 + (((P.focal / 3) | 0) - 1) * 0.3)
  const rad2 = 0.45 * m * (0.45 * m)
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let xs = x
      let ys = y
      if (P.focus > 0.01) {
        const dx = x - cxp
        const dy = y - cyp
        const sc = 1 - P.focus * 0.55 * Math.exp(-(dx * dx + dy * dy) / rad2)
        xs = cxp + dx * sc
        ys = cyp + dy * sc
      }
      F[y * w + x] =
        form.field((xs / m) * 1.5, (ys / m) * 1.5, seed, P) +
        P.drift * 0.45 * (ys / m)
    }
  const GM = new Float32Array(w * h)
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x
      const gx = (F[x < w - 1 ? i + 1 : i] - F[x > 0 ? i - 1 : i]) * 0.5
      const gy = (F[y < h - 1 ? i + w : i] - F[y > 0 ? i - w : i]) * 0.5
      GM[i] = Math.sqrt(gx * gx + gy * gy) + 1e-6
    }

  // Dry-roller lanes (impression stage, shared by every screen)
  const ang = (P.laneAngle * Math.PI) / 180
  const ldx = Math.cos(ang)
  const ldy = Math.sin(ang)
  const pMin = Math.min(0, (w - 1) * ldx) + Math.min(0, (h - 1) * ldy)
  const pMax = Math.max(0, (w - 1) * ldx) + Math.max(0, (h - 1) * ldy)
  const LN = Math.ceil(pMax - pMin) + 2
  const stripe = new Float32Array(LN)
  for (let i = 0; i < LN; i++) {
    const u = (i / m) * P.lanes
    const v =
      vn(u, 0.5, seed + 601) * 0.65 + vn(u * 2.7, 3.5, seed + 619) * 0.35
    stripe[i] = clampN((v - (1 - P.laneCover)) / 0.28, 0, 1)
  }

  // SEPARATION: plate list with registration offsets; 1 ink + register =
  // ghost double-strike
  interface Plate {
    inkIdx: number
    A: Rgb
    B: Rgb
    ox: number
    oy: number
    mseed: number
  }
  const paper = hex(way.paper)
  const nI = way.inks.length
  const regPx = P.register * 8 * s01
  let plates: Plate[]
  if (nI === 1 && P.register > 0.02) {
    const a = hex(way.inks[0][0])
    const gA: Rgb = [0, 1, 2].map((i) =>
      Math.round(a[i] + (paper[i] - a[i]) * 0.5),
    ) as Rgb
    const th = hash2(1, 77, seed) * 6.283
    plates = [
      {
        inkIdx: 0,
        A: gA,
        B: gA,
        ox: Math.round(Math.cos(th) * regPx),
        oy: Math.round(Math.sin(th) * regPx),
        mseed: seed + 55,
      },
      {
        inkIdx: 0,
        A: hex(way.inks[0][0]),
        B: hex(way.inks[0][1]),
        ox: 0,
        oy: 0,
        mseed: seed + 55,
      },
    ]
  } else {
    plates = way.inks.map((r, i) => {
      const th = hash2(i, 77, seed) * 6.283
      const rg = i === 0 ? 0 : regPx
      return {
        inkIdx: i,
        A: hex(r[0]),
        B: hex(r[1]),
        ox: Math.round(Math.cos(th) * rg),
        oy: Math.round(Math.sin(th) * rg),
        mseed: seed + 55 + i * 17,
      }
    })
  }
  const isBands = form.sep === "bands"
  const SL = SLICES[nI] ?? SLICES[1]
  const dens = form.dens ? form.dens(P.busy) : P.busy
  const screen = SCREENS[P.screen] ?? SCREENS.solid
  const E: ScreenEnv = {
    seed,
    s01,
    worn: P.worn,
    edge: (3 + P.worn * 18) * s01,
    clean: 2.5 * s01,
    ldx,
    ldy,
  }
  const img = ctx.createImageData(w, h)
  const D = img.data
  const nP = plates.length
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x
      let r = paper[0]
      let g = paper[1]
      let b = paper[2]
      for (let q = 0; q < nP; q++) {
        const pl = plates[q]
        let xx = x + pl.ox
        let yy = y + pl.oy
        xx = xx < 0 ? 0 : xx >= w ? w - 1 : xx
        yy = yy < 0 ? 0 : yy >= h ? h - 1 : yy
        const j = yy * w + xx
        let sd: number
        if (isBands) {
          const t = F[j] * dens
          const bnd = Math.floor(t)
          const tf = t - bnd
          let d: number
          let bi: number
          if (tf < 0.5) {
            d = Math.min(tf, 0.5 - tf)
            bi = bnd
          } else {
            d = -Math.min(tf - 0.5, 1 - tf)
            bi = tf < 0.75 ? bnd : bnd + 1
          }
          if (((bi % nI) + nI) % nI !== pl.inkIdx) continue
          sd = d / (GM[j] * dens + 1e-6)
        } else {
          const v = F[j]
          const sl = SL[pl.inkIdx]
          sd = Math.min(v - sl[0], sl[1] - v) / GM[j]
        }
        const a = clampN(0.5 + sd, 0, 1)
        if (a <= 0.004) continue
        // Ink tone: two-stop ramp under low-frequency mottle
        const mo = fbm((x / m) * 4.8 + q * 3.7, (y / m) * 4.8, pl.mseed, 2)
        const k = 0.1 + 0.8 * mo
        let ir = pl.A[0] + (pl.B[0] - pl.A[0]) * k
        let ig = pl.A[1] + (pl.B[1] - pl.A[1]) * k
        let ib = pl.A[2] + (pl.B[2] - pl.A[2]) * k
        // IMPRESSION: screening decides where paper punches back through
        if (a > 0.55) {
          const lane = stripe[(x * ldx + y * ldy - pMin) | 0] || 0
          if (screen.punch(sd, lane, x, y, q, E)) {
            ir = paper[0] * 0.88 + ir * 0.12
            ig = paper[1] * 0.88 + ig * 0.12
            ib = paper[2] * 0.88 + ib * 0.12
          }
        }
        r = r + (ir - r) * a
        g = g + (ig - g) * a
        b = b + (ib - b) * a
      }
      // SUBSTRATE: paper tooth over everything
      const gr =
        (vn((x * 0.9) / s01, (y * 0.9) / s01, seed + 777) - 0.5) * P.grain * 30
      const p4 = i * 4
      D[p4] = clampN(r + gr, 0, 255)
      D[p4 + 1] = clampN(g + gr, 0, 255)
      D[p4 + 2] = clampN(b + gr, 0, 255)
      D[p4 + 3] = 255
    }
  ctx.putImageData(img, 0, 0)

  if (isBands) {
    // Island dots where the ribbon runs wide
    const n = Math.max(4, Math.round(7 * s01))
    ctx.fillStyle = way.paper
    for (let k2 = 0; k2 < n; k2++) {
      const x = Math.floor(hash2(k2, 3, seed + 401) * w)
      const y = Math.floor(hash2(k2, 9, seed + 409) * h)
      const i = y * w + x
      const t = F[i] * dens
      const tf = t - Math.floor(t)
      if (tf >= 0.5) continue
      const dpx = Math.min(tf, 0.5 - tf) / (GM[i] * dens + 1e-6)
      const rad = 0.012 * m * (0.7 + hash2(k2, 17, seed + 421))
      if (dpx < rad * 4) continue
      ctx.beginPath()
      ctx.arc(x, y, rad, 0, Math.PI * 2)
      ctx.fill()
    }
  }
}

/**
 * Paint a recipe into a canvas at the given size, then apply the format's
 * post-process (disc mask, card frame) when `post` is on.
 */
export function paintRecipe(
  cv: HTMLCanvasElement,
  recipe: Recipe,
  w: number,
  h: number,
  post = false,
) {
  const way = effectiveWay(recipe)
  paint(cv, w, h, recipe.p, way, recipe.seed)
  const fmt = FORMATS[recipe.format]
  if (post && fmt.post) {
    const ctx = cv.getContext("2d")
    if (ctx) fmt.post(ctx, w, h, way)
  }
}

/** Preview size for a format, fitting the longest edge to `fit` px. */
export function fitFormat(format: FormatKey, fit: number) {
  const F0 = FORMATS[format]
  const sc = fit / Math.max(F0.w, F0.h)
  return { w: Math.round(F0.w * sc), h: Math.round(F0.h * sc) }
}

/**
 * Sibling pressing `i` of a recipe: same recipe with deterministic drift on
 * roller angle, drift and molten, plus a derived seed. Adopting one is
 * lossless — it is a full recipe of its own.
 */
export function seriesVariant(recipe: Recipe, i: number): Recipe {
  const { p, seed } = recipe
  return {
    ...recipe,
    seed: seed + i * 137,
    p: {
      ...p,
      laneAngle: (p.laneAngle + (hash2(i, 1, seed) - 0.5) * 60 + 360) % 180,
      drift: clampN(p.drift + (hash2(i, 2, seed) - 0.5) * 0.5, -1, 1),
      molten: clampN(p.molten + (hash2(i, 3, seed) - 0.5) * 0.2, 0, 1),
    },
  }
}

/** Apply an archetype: its params over the current ones, its way, plate order reset. */
export function applyArchetype(recipe: Recipe, key: ArchetypeKey): Recipe {
  const ar = ARCHETYPES[key]
  return { ...recipe, way: ar.way, perm: 0, p: { ...recipe.p, ...ar.p } }
}

/** Short human label for a recipe: `tide p2 · 7412`. */
export function recipeLabel(recipe: Pick<Recipe, "way" | "perm" | "seed">) {
  const list = permsFor(WAYS[recipe.way])
  const perm = list ? ` p${(recipe.perm % list.length) + 1}` : ""
  return `${recipe.way}${perm} · ${recipe.seed}`
}

/** Export filename for a recipe: `tide-p2-7412-cover.png`. */
export function recipeFilename(recipe: Recipe) {
  const list = permsFor(WAYS[recipe.way])
  const perm = list ? `-p${(recipe.perm % list.length) + 1}` : ""
  return `${recipe.way}${perm}-${recipe.seed}-${recipe.format}.png`
}

// ---------------------------------------------------------------------------
// Recipe codec — a recipe round-trips through flat string params so a studio
// URL is a complete, shareable replication of a cover.

const NUMERIC_KEYS = [
  "busy",
  "molten",
  "drift",
  "focus",
  "focal",
  "worn",
  "lanes",
  "laneCover",
  "laneAngle",
  "register",
  "grain",
] as const

type NumericKey = (typeof NUMERIC_KEYS)[number]

/** Slider ranges, also used to clamp whatever arrives in a URL. */
export const PARAM_RANGES: Record<
  NumericKey,
  { min: number; max: number; step: number }
> = {
  busy: { min: 2, max: 9, step: 1 },
  molten: { min: 0, max: 1, step: 0.05 },
  drift: { min: -1, max: 1, step: 0.05 },
  focus: { min: 0, max: 1, step: 0.05 },
  focal: { min: 0, max: 8, step: 1 },
  worn: { min: 0, max: 1, step: 0.05 },
  lanes: { min: 2, max: 30, step: 1 },
  laneCover: { min: 0.1, max: 1, step: 0.05 },
  laneAngle: { min: 0, max: 180, step: 5 },
  register: { min: 0, max: 1, step: 0.05 },
  grain: { min: 0, max: 1, step: 0.05 },
}

export const RECIPE_PARAM_KEYS = [
  "way",
  "perm",
  "seed",
  "format",
  "pattern",
  "screen",
  ...NUMERIC_KEYS,
] as const

export type RecipeParams = Partial<
  Record<(typeof RECIPE_PARAM_KEYS)[number], string | null>
>

function isKey<T extends string>(
  table: Record<T, unknown>,
  k: string | null | undefined,
): k is T {
  return k != null && Object.prototype.hasOwnProperty.call(table, k)
}

function num(v: string | null | undefined, fallback: number) {
  if (v == null || v === "") return fallback
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Rebuild a recipe from flat params (a URL, an Inertia prop). Anything
 * missing or out of range falls back to the default, so a hand-edited or
 * stale link still paints.
 */
export function recipeFromParams(
  params: RecipeParams | null | undefined,
): Recipe {
  const base = DEFAULT_RECIPE
  if (!params) return { ...base, p: { ...base.p } }
  const way = isKey(WAYS, params.way) ? params.way : base.way
  const perms = permsFor(WAYS[way])
  const perm = perms
    ? ((Math.trunc(num(params.perm, 0)) % perms.length) + perms.length) %
      perms.length
    : 0
  const p: Params = {
    ...base.p,
    pattern: isKey(FORMS, params.pattern) ? params.pattern : base.p.pattern,
    screen: isKey(SCREENS, params.screen) ? params.screen : base.p.screen,
  }
  for (const k of NUMERIC_KEYS) {
    const { min, max, step } = PARAM_RANGES[k]
    const v = clampN(num(params[k], base.p[k]), min, max)
    p[k] = step >= 1 ? Math.round(v) : v
  }
  return {
    way,
    perm,
    seed: Math.max(0, Math.trunc(num(params.seed, base.seed))),
    format: isKey(FORMATS, params.format) ? params.format : base.format,
    p,
  }
}

/** Flatten a recipe to string params. Floats are trimmed to 2 dp. */
export function recipeToParams(recipe: Recipe): Record<string, string> {
  const out: Record<string, string> = {
    way: recipe.way,
    perm: String(recipe.perm),
    seed: String(recipe.seed),
    format: recipe.format,
    pattern: recipe.p.pattern,
    screen: recipe.p.screen,
  }
  for (const k of NUMERIC_KEYS) {
    const v = recipe.p[k]
    out[k] = Number.isInteger(v) ? String(v) : v.toFixed(2)
  }
  return out
}

export function recipeToQuery(recipe: Recipe): string {
  return new URLSearchParams(recipeToParams(recipe)).toString()
}
